<template>
  <div class="w-100 col-12">
    <div class="mb-2">
      <!-- begin:Show and hide map -->
      <div class="d-inline cursor-pointer text-color5" @click="hideMap($event)">
        <span v-b-tooltip.hover.html :title="$t('tooltips.radius_location')"><i class="fa fa-info-circle font-size-xRegular me-3"></i></span>
        <span> {{ isMapVisible ? $t('hide_map') : $t('show_map') }} </span>
        <i :class="{
          'fa fa-chevron-down': true,
          'arrow': true,
          'rotated': !isMapVisible,
          'text-color5': true
        }"
        ></i>
      </div>
      <!-- end:Show and hide map -->

      <!-- google maps rendering div -->
      <div id="map" class="map-frame"></div>
    </div>

    <!-- SELECTED LOCATION CHIPS -->
    <Chip
      v-if="showChips"
      :data="userMarkerLocations"
      @update-location="removeCircleFromMap"
    >
    </Chip>
    <!-- END SELECTED LOCATION CHIPS -->

    <template v-if="usePlacesApi">
      <!-- LABELS LINE -->
      <div class="mt-4 col-12 d-inline-flex">
        <div class="col-3">
          <label class="form-label fw-bold">{{ $t('radius_coverage') }}*</label>
        </div>
        <div class="col-1"></div>
        <div class="col-8">
          <label class="form-label fw-bold">
            {{  $t('desired_location') }}*
          </label>
        </div>
      </div>
      <!-- END LABELS LINE -->
      <!-- INPUTS LINE -->
      <div class="col-12 d-inline-flex">
        <!-- RADIUS -->
        <div class="col-3">
          <div class="form-group mb-3">
            <div class="custom-select">
              <select
                class="form-control"
                v-model.trim="radius"
              >
                <option value="5" selected="selected">5 KM</option>
                <option value="10">10 KM</option>
                <option value="15">15 KM</option>
                <option value="20">20 KM</option>
              </select>
            </div>
          </div>
        </div>
        <!-- END RADIUSSS -->
        <div class="col-1"></div>
        <!-- PLACES API SEARCH FIELD -->
        <div class="col-8">
          <input v-model="typedAddress" class="form-control" id="place" type="text" :placeholder="$t('enter_address')" />
        </div>
        <!-- PLACES API SEARCH FIELD -->
      </div>
    </template>
    <!-- END INPUTS LINE -->
  </div>
</template>
<script>
import { Loader } from '@googlemaps/js-api-loader'
import variables from '@/mixins/variables'
import Chip from '@/components/common/Chip/Chip'

export default {
  mixins: [variables],
  props: ['elements', 'selectedElements', 'inventoryManagement', 'showChips', 'usePlacesApi'],
  name: 'GoogleMapsComponent',
  components: {
    Chip
  },
  data () {
    return {
      radius: 5,
      //  Google API loader instance
      google: '',
      //  rendered map on screen
      map: '',
      //  pinned marker locations on map
      markers: [],
      //  user's location on map (used to center and zoom)
      userLocation: null,
      //  pinned marker on map
      userMarker: null,
      //  pinned locations on map (marker or radius)
      userMarkerLocations: [],
      //  circle bounds coordinates of pinned radius location
      radiusLocation: [],
      //  address user types in places API input
      typedAddress: '',
      //  radius location circles added on map
      addedCircle: [],
      isMapVisible: true
    }
  },
  async mounted () {
    const loader = new Loader({
      apiKey: process.env.VUE_APP_GCP_API_KEY,
      version: 'weekly',
      libraries: ['places', 'marker', 'geometry'] // *IF NEW LIB IS REQUIRED, ADD HERE
    })
    // Loads Google's MAP
    this.google = await loader.load()
    this.initializeMap()
    if (this.usePlacesApi) this.setupAutocomplete()

    //  If there are initial elements coming from parent element
    if (this.elements && this.elements.length > 0) {
      //  Set first location as user's location
      this.userLocation = this.userMarkerLocations[0]

      //  If Maps is not used to manage inventory, e.g. OOH inventory
      if (!this.inventoryManagement) {
        this.elements.filter(element => {
          // Validates coordinates as data could be from different sources
          const coordinates = {
            lat: element?.coords?.lat ?? element?.lat,
            lng: element?.coords?.lng ?? element?.lng
          }

          const myLatLng = new this.google.maps.LatLng(coordinates.lat, coordinates.lng)

          this.map.setCenter(myLatLng)
          this.map.setZoom(10)

          //  Sets current triggered event's circle
          this.circle = new this.google.maps.Circle({
            map: this.map,
            center: myLatLng,
            radius: this.radius * 1000,
            fillColor: 'yellow',
            fillOpacity: 0.2,
            strokeColor: 'black',
            strokeOpacity: 0.6,
            strokeWeight: 2
          })

          element.circle = this.circle
          this.userMarkerLocations.push(element)
          this.addedCircle.push(this.circle)
          this.map.fitBounds(this.circle.getBounds())
          this.handleRadiusLocation(this.circle.getBounds())
        })
      }
    }
  },
  watch: {
    elements (loadedElements) {
      //  Removes marker if user searches again
      if (this.markers.length > 0) {
        this.markers.map((marker) => {
          marker.setMap(null)
        })
      }
      //  Pin elements on rendering
      if (this.inventoryManagement) this.pinElementsOnMap(loadedElements)
      if (this.inventoryManagement) this.pinElementsOnMap(loadedElements)
    },
    userLocation (newUserLocation) {
      this.$emit('update-user-location', newUserLocation)
    },
    userMarkerLocations (newMarkerLocation) {
      this.$emit('update-marker-locations', newMarkerLocation)
    },
    radiusLocation (newRadiusBound) {
      this.$emit('update-radius-location', newRadiusBound)
    }
  },
  methods: {
    hideMap (element) {
      const mapElement = document.getElementById('map')
      if (mapElement) {
        this.isMapVisible = !this.isMapVisible
        mapElement.classList.toggle('map-frame--hidden')
      }
    },
    /**
     * Initialize google maps
     */
    initializeMap () {
      const center = { lat: -23.553610, lng: -46.637101 }
      this.map = new this.google.maps.Map(document.getElementById('map'), {
        center,
        zoom: 12,
        mapId: process.env.VUE_APP_GOOGLE_MAPS_MAP_ID
      })
    },
    /**
     * Initialize autocomplete from Places API
     */
    setupAutocomplete () {
      const input = document.getElementById('place')
      const options = {
        bounds: {
          north: -23.451, south: -23.671, east: -46.585, west: -46.805
        },
        types: ['establishment', 'geocode'],
        componentRestrictions: { country: ['us', 'br', 'ar', 'py', 'pe', 'mx', 'co', 'fr', 'nl', 'be'] },
        fields: ['address_component', 'geometry', 'icon', 'name']
      }

      const autocomplete = new this.google.maps.places.Autocomplete(input, options)
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        if (place.geometry) {
          //  Set up map initial settings
          this.userLocation = place
          this.map.setCenter(place.geometry.location)
          this.map.setZoom(15)

          //  Removes marker if user searches again
          if (this.userMarker !== null) this.userMarker.setMap(null)
          // eslint-disable-next-line no-new
          this.userMarker = new this.google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: place.geometry.location,
            title: 'Your location'
          })

          //  If component if for regular use (location and radius bounds)
          if (!this.inventoryManagement) {
            //  Sets current triggered event's circle
            this.circle = new this.google.maps.Circle({
              map: this.map,
              center: place.geometry.location,
              radius: this.radius * 1000,
              fillColor: 'yellow',
              fillOpacity: 0.2,
              strokeColor: 'black',
              strokeOpacity: 0.6,
              strokeWeight: 2
            })
            this.addedCircle.push(this.circle)
            this.map.fitBounds(this.circle.getBounds())
            this.handleRadiusLocation(this.circle.getBounds())
          }

          //  Refined data to be pushed into user_marker's array index
          const dataToBePushed = {
            state: place.address_components.find(addresses => addresses.types[0] === 'administrative_area_level_1'),
            city: place.address_components.find(addresses => addresses.types[0] === 'administrative_area_level_2'),
            country: place.address_components.find(addresses => addresses.types[0] === 'country'),
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            name: place.name,
            circle: this.circle ?? null,
            radius: this.radius
          }

          this.userMarkerLocations.push(dataToBePushed)
        }

        //  Clears input after location is added
        this.typedAddress = ''
      })
    },
    /**
     * Pin elements on map using Markers custom markers
     * @param elements
     * @param fromAutoComplete
     * @returns {Promise<void>}
     */
    async pinElementsOnMap (elements, fromAutoComplete = true) {
      if (this.markers.length > 0) this.cleanMarkers()
      elements.forEach((element, index) => {
        let myLatLng
        if (!fromAutoComplete) {
          myLatLng = new this.google.maps.LatLng(element.latitude ?? element.lat, element.longitude ?? element.lng)
        } else {
          myLatLng = element
        }

        //  Calculates distance based on provided radius and coordinate
        const distanceInKm = this.getElementDistanceFromUserLocation(element)
        if (distanceInKm <= this.radius) {
          // eslint-disable-next-line no-new
          this.elements[index].mapMarking = new this.google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: myLatLng,
            content: this.buildPersonalizedMarker(element)
          })
          this.elements[index].mapMarking.addListener('click', () => {
            this.toggleHighlight(this.elements[index].mapMarking, element)
          })
          this.markers.push(this.elements[index].mapMarking)
        }
      })
    },
    /**
     * Calculates distance between user to location based on given coordinates
     */
    getElementDistanceFromUserLocation (element) {
      const centerLatLng = new this.google.maps.LatLng(
        this.userLocation?.geometry?.location?.lat() ?? element.lat,
        this.userLocation?.geometry?.location?.lng() ?? element.lng
      )
      const elementLatLng = new this.google.maps.LatLng(element.latitude ?? element.lat, element.longitude ?? element.lng)
      const distanceInMeters = this.google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, elementLatLng)
      return distanceInMeters / 1000
    },
    /**
     *  Create personalized map marker using HTML
     */
    buildPersonalizedMarker (element) {
      const content = document.createElement('div')
      content.classList.add('property')
      let icon
      if (this.selectedElements.includes(element.id)) {
        icon = `
        <div class="icon p-2" style="padding: 10px;border-radius: 25px;background-color: green;width: 45px;height: 45px;margin:auto;">
          <img alt="logo pixelroads" aria-hidden="true" src="/images/favicon/favicon-32.png" style="width: 30px;height: 30px;margin: auto;" title="123">
        </div>
        `
      } else {
        icon = `
          <div class="icon p-2">
            <img alt="logo pixelroads" aria-hidden="true" src="/images/favicon/favicon-32.png" style="width: 30px;height: 30px;margin: auto;" title="123">
          </div>
        `
      }
      content.innerHTML = icon + `
    <div class="details">
      <div class="card-content" style="display:flex;">
        <div class="picture" style="width: 105px;">
          <img alt="" aria-hidden="true" src="${element.image}" style="height: 183px;width: 105px;border-bottom-left-radius: 8px;">
        </div>
        <div class="adress-group" style="padding: 5px;">
          <div class="address">
            <p style="font-size: 15px;color: black;">${element.name}</p>
            <div class="item" style="color: #4d4d4d;">
                <span style="font-size: 13px;">${element.address}, ${element.number}</span>
                <p style="font-size: 13px;">${element.region} - ${element.state_short_name}</p>
            </div>
          </div>
          <div class="features">
            <div class="item">
              <i aria-hidden="true" class="fa fa-building" title="Towers"></i>
              <span>${element.towers}</span>
            </div>
            <div class="item">
              <i aria-hidden="true" class="fa fa-desktop" title="Screens"></i>
              <span>${element.screens}</span>
            </div>
            <div class="item">
              <i aria-hidden="true" class="fa fa-user" title="Impacted"></i>
              <span>${element.target_audience}</span>
            </div>
            <div class="item">
              <i aria-hidden="true" class="fa fa-building" title="Apartments"></i>
              <span>${element.apartments}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    `
      return content
    },
    /**
     * toggle element on map
     * @param markerView
     */
    toggleHighlight (markerView) {
      if (markerView.content.classList.contains('highlight')) {
        markerView.content.classList.remove('highlight')
        markerView.zIndex = null
      } else {
        markerView.content.classList.add('highlight')
        markerView.zIndex = 1
      }
    },
    /**
     * zooms into selected element
     * @param element
     */
    focusOnElement (element) {
      if (element.constructor === Array) {
        const center = this.calculateMapAverageCenter(element)
        this.map.setCenter(center)
        this.map.setZoom(13)
        element.map(apartment => {
          this.toggleHighlight(apartment.mapMarking, apartment)
        })
      } else {
        const center = { lat: element.lat + 0.01, lng: element.lng }
        this.map.setCenter(center)
        this.map.setZoom(14)
        this.toggleHighlight(element.mapMarking, element)
      }
    },
    /**
     * Calculates average coordinates between locations
     * @param locations
     * @returns {{lng: number, lat: number}}
     */
    calculateMapAverageCenter (locations) {
      const total = locations.length
      const averageLat = locations.reduce((sum, loc) => sum + loc.lat, 0) / total
      const averageLng = locations.reduce((sum, loc) => sum + loc.lng, 0) / total
      return { lat: averageLat, lng: averageLng }
    },
    /**
     * Removes pinned markers
     */
    cleanMarkers () {
      this.markers.map((marker) => {
        marker.setMap(null)
      })
    },
    /*
     * Handle provided RadiusLocation, removing repeated values
     */
    handleRadiusLocation (value) {
      if (this.radiusLocation.length > 0) {
        const index = this.radiusLocation.findIndex(item => JSON.stringify(item) === JSON.stringify(value))
        if (index > -1) {
          this.radiusLocation.splice(index, 1)
        } else {
          this.radiusLocation.push(value)
        }
      } else {
        this.radiusLocation.push(value)
      }
    },
    /**
     * Remove selected radius circle and marker location
     * @param param
     */
    removeCircleFromMap (param) {
      if (param.circle) {
        param.circle.setMap(null)
      }

      //  Find clicked circle's index in userMarkerLocation Array
      const index = this.userMarkerLocations.findIndex(item => item.circle === param.circle)

      if (index !== -1) {
        this.userMarkerLocations.splice(index, 1)
      }
    }
  }
}
</script>
<style>
.arrow {
  transition: transform 0.6s ease
}

.arrow.rotated {
  transform: rotate(-180deg);
}

#map {
  border: 1px solid #ccc;
  border-radius: 5px;
}
.map-frame {
  height: 400px;
  width: 100%;
  margin-top: 20px;
  transition: all 0.6s ease-out;
  overflow: hidden;
}

.map-frame--hidden {
  height: 0;
  margin: 0;
  opacity: 0;
}

/*
 * Property styles in unhighlighted state.
 */
.property {
  align-items: center;
  background-color: #FFFFFF;
  border-radius: 50%;
  color: #263238;
  font-size: 14px;
  gap: 15px;
  height: 40px;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease-out;
  width: 40px;
}
.property::after {
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #FFFFFF;
  content: "";
  height: 0;
  left: 50%;
  position: absolute;
  top: 95%;
  transform: translate(-50%, 0);
  transition: all 0.3s ease-out;
  width: 0;
  z-index: 1;
}
.property .icon {
  align-items: center;
  display: flex;
  justify-content: center;
  color: black;
}
.property .icon svg {
  height: 20px;
  width: auto;
}
.property .details {
  display: none;
  flex-direction: column;
  flex: 1;
}
.property .address {
  color: #9E9E9E;
  font-size: 10px;
}
.property .features {
  display: flex;
  gap: 10px;
  .item {
    max-width: 90px;
    margin-bottom: 8px;
  }
}
.property .features > div {
  align-items: center;
  background: #FEFDF5;
  border-radius: 5px;
  border: 1px solid #ccc;
  display: flex;
  font-size: 13px;
  gap: 5px;
  padding: 5px;
  i {
    font-size: 20px;
  }
}
/*
 * Property styles in highlighted state.
 */
.property.highlight {
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 10px 10px 5px rgba(0, 0, 0, 0.2);
  height: 230px;
  width: 360px;
}
.property.highlight::after {
  border-top: 9px solid #FFFFFF;
}
.property.highlight .details {
  display: flex; flex-direction: column; border: 1px solid #ddd; border-radius: 8px;
}
.property.highlight .icon {
  background-color: white !important;
  svg {
    width: 50px;
    height: 50px;
  }
}
/*
 * House icon colors.
 */
.property.highlight:has(.fa-house) .icon {
  color: var(--house-color);
}
.property:not(.highlight):has(.fa-house) {
  background-color: var(--house-color);
}
.property:not(.highlight):has(.fa-house)::after {
  border-top: 9px solid var(--house-color);
}
/*
 * Building icon colors.
 */
.property.highlight:has(.fa-building) .icon {
  color: var(--building-color);
}
.property:not(.highlight):has(.fa-building) {
  background-color: var(--building-color);
}
.property:not(.highlight):has(.fa-building)::after {
  border-top: 9px solid var(--building-color);
}
/*
 * Warehouse icon colors.
 */
.property.highlight:has(.fa-warehouse) .icon {
  color: var(--warehouse-color);
}
.property:not(.highlight):has(.fa-warehouse) {
  background-color: var(--warehouse-color);
}
.property:not(.highlight):has(.fa-warehouse)::after {
  border-top: 9px solid var(--warehouse-color);
}
/*
 * Shop icon colors.
 */
.property.highlight:has(.fa-shop) .icon {
  color: var(--shop-color);
}
.property:not(.highlight):has(.fa-shop) {
  background-color: var(--shop-color);
}
.property:not(.highlight):has(.fa-shop)::after {
  border-top: 9px solid var(--shop-color);
}
</style>
